import moment from "moment";

/**
 * Parse a date input (string or Date) as a local date and return a Date object
 * representing that date at local midnight.
 *
 * This fixes the issue where date strings like "06/05/2010" or "2010-06-05"
 * are interpreted as UTC by the Date constructor, causing timezone shifts.
 *
 * @param dateInput - Date string or Date object
 * @returns Date object at local midnight for the given date
 */
export function parseAsLocalDate(
  dateInput: string | Date | null | undefined
): Date | null {
  if (!dateInput) return null;

  let m: moment.Moment;

  if (dateInput instanceof Date) {
    // If it's already a Date object, it may have been parsed as UTC by MUI
    // Extract the UTC date components and create a local date with those values
    // This treats the UTC date "2010-06-05" as local date "2010-06-05"
    m = moment([
      dateInput.getUTCFullYear(),
      dateInput.getUTCMonth(),
      dateInput.getUTCDate(),
    ]);
  } else {
    // If it's a string, parse it in local time
    m = moment(dateInput);
  }

  if (!m.isValid()) return null;

  // Set to start of day in local time and return as Date object
  return m.startOf("day").toDate();
}

/**
 * Convert a local date to UTC date range (start and end of day)
 * This is used for "is" or "equals" operators to match the entire day
 *
 * @param localDate - Date object in local time
 * @returns Object with start and end Date objects in UTC
 */
export function localDateToUTCRange(localDate: Date): {
  start: Date;
  end: Date;
} {
  const m = moment(localDate);

  // Get start of day in local time, then convert to UTC
  const startOfDayLocal = m.clone().startOf("day");
  const startUTC = startOfDayLocal.utc().toDate();

  // Get end of day in local time, then convert to UTC
  const endOfDayLocal = m.clone().endOf("day");
  const endUTC = endOfDayLocal.utc().toDate();

  return { start: startUTC, end: endUTC };
}
